import { useEffect, useState } from 'react';
import { Control, DeepMap, FieldError, UseFormSetValue } from 'react-hook-form';

import { Tokens } from 'config';
import { getDrugIsExpired, Enums } from 'utils';
import { useUserContext } from 'hooks';
import { Controlled } from 'forms';
import UI from 'ui';
import { StyledTaskForm } from '../TaskForm';
import StaticStandingOrders from '../StaticStandingOrders';
import TaskHeader, { TaskHeaderProps } from '../TaskHeader';
import AdditionalPhotoDocSection from '../AdditionalPhotoDocSection';
import { POCTestCategoryToLookupIdEnum, TInputs } from './utils';
import { EncounterTask, EncounterTaskStatusEnum } from 'generated/graphql';
import {
  renderClinicianAttestation,
  StyledClinicianAttestation,
} from 'modules/Patient/utils';

const PointOfCareTestForm = ({
  control,
  errors,
  formState,
  inputsAreDisabled,
  isOrderedModule = false,
  resultIsCritical = false,
  setValue,
  taskHeaderProps,
}: {
  control: Control<TInputs>;
  errors: DeepMap<TInputs, FieldError>;
  formState: TInputs;
  inputsAreDisabled: boolean;
  isOrderedModule?: boolean;
  resultIsCritical?: boolean;
  setValue: UseFormSetValue<TInputs>;
  taskHeaderProps: TaskHeaderProps;
}): JSX.Element => {
  const { task } = taskHeaderProps;
  const { operatingAsClinician } = useUserContext() ?? {};
  const lookupId = task?.pointOfCareTestLookup?.pointOfCareTestLookupId;
  const hasStandingOrderOptions =
    task?.pointOfCareTestLookup?.standingOrders &&
    task.pointOfCareTestLookup.standingOrders.length > 0;
  const [correctionsWorkflow, setCorrectionsWorkflow] = useState(false);

  const {
    clinicianFirstName,
    clinicianLastName,
    expirationDate,
    humidity,
    internalControlAcceptable,
    result,
    standingOrderAddedAt,
    standingOrderId,
    temperature,
  } = formState;

  const outOfRangeAlertMessage = (type: string) =>
    `Because the current room/ambient ${type} is out of range, you must move to a different testing environment before you can administer this test to the patient.`;
  const internalControlsAlertMessage =
    'Because the internal controls are not acceptable, you will need to repeat testing. If this occurs again, contact your supervisor';

  const outOfRangeTemperature = Boolean(
    (temperature && Number(temperature) < 59) ||
      (temperature && Number(temperature) > 86),
  );
  const outOfRangeHumidity = Boolean(
    (humidity && Number(humidity) < 20) || (humidity && Number(humidity) > 80),
  );
  const reagentIsExpired = Boolean(
    expirationDate && getDrugIsExpired(task, expirationDate),
  );

  const inputsAreDisabledAndNotCorrectable =
    inputsAreDisabled && !correctionsWorkflow;

  const inputsAreDisabledOrNoClinician =
    (inputsAreDisabled || !clinicianFirstName || !clinicianLastName) &&
    !correctionsWorkflow;

  const qualityControlsAreFailing =
    inputsAreDisabledOrNoClinician ||
    outOfRangeTemperature ||
    outOfRangeHumidity ||
    reagentIsExpired;

  const getSelectOptions = () => {
    switch (lookupId) {
      case POCTestCategoryToLookupIdEnum.Covid:
        return [
          { label: 'Positive', value: 'positive' },
          { label: 'Negative', value: 'negative' },
          { label: 'Invalid', value: 'invalid' },
        ];
      case POCTestCategoryToLookupIdEnum.FluAntigen:
        return [
          { label: 'Flu A + / Flu B -', value: 'aPositiveBNegative' },
          { label: 'Flu A - / Flu B +', value: 'aNegativeBPositive' },
          { label: 'Flu A - / Flu B -', value: 'aNegativeBNegative' },
          { label: 'Invalid', value: 'invalid' },
        ];
      case POCTestCategoryToLookupIdEnum.Pregnancy:
        return [
          { label: 'Pregnant', value: 'pregnant' },
          { label: 'Not Pregnant', value: 'notPregnant' },
          { label: 'Invalid', value: 'invalid' },
        ];
      case POCTestCategoryToLookupIdEnum.Rsv:
      case POCTestCategoryToLookupIdEnum.Strep:
        return [
          { label: 'Positive', value: 'positive' },
          { label: 'Negative', value: 'negative' },
          { label: 'Control Invalid', value: 'controlInvalid' },
        ];
      default:
        return [{ label: 'Unknown', value: 'unknown' }];
    }
  };

  const getSelectRightLabel = () => {
    switch (lookupId) {
      case POCTestCategoryToLookupIdEnum.A1c:
        return 'mmol/l';
      case POCTestCategoryToLookupIdEnum.A1cPercent:
        return '% A1C';
      case POCTestCategoryToLookupIdEnum.Glucose:
        return 'mg/dl';
      default:
        return '';
    }
  };

  useEffect(() => {
    setCorrectionsWorkflow(false);
  }, [task?.corrections]);

  const renderResultTypePerPOCTLookupId = () => {
    switch (lookupId) {
      case POCTestCategoryToLookupIdEnum.Covid:
      case POCTestCategoryToLookupIdEnum.FluAntigen:
      case POCTestCategoryToLookupIdEnum.Pregnancy:
      case POCTestCategoryToLookupIdEnum.Rsv:
      case POCTestCategoryToLookupIdEnum.Strep:
        return (
          <>
            <Controlled.Select
              control={control}
              disabled={qualityControlsAreFailing}
              label="Result"
              name="result"
              options={getSelectOptions()}
              sortedOptions={false}
            />

            {result && ['invalid', 'controlInvalid'].includes(result) && (
              <div style={{ marginTop: `calc(${Tokens.rhythm} * 2)` }}>
                <UI.AlertBox
                  label="Internal controls not acceptable"
                  message={internalControlsAlertMessage}
                  severity={UI.AlertBox.SeverityLevel.Danger}
                />
              </div>
            )}
          </>
        );
      default:
        return (
          <Controlled.Input
            control={control}
            defaultValue=""
            disabled={qualityControlsAreFailing}
            fullWidth
            label="Result"
            name="result"
            rightLabel={getSelectRightLabel()}
          />
        );
    }
  };

  const renderCriticalValueConfirmation = () => {
    if (
      !task?.pointOfCareTest?.criticalValueConfirmations ||
      task?.pointOfCareTest?.criticalValueConfirmations.length < 1
    )
      return null;

    switch (task?.pointOfCareTest?.criticalValueConfirmations.length) {
      case 1:
        return (
          <p>
            {Enums.humanize(
              task?.pointOfCareTest?.criticalValueConfirmations[0]
                ?.operatingAsRole as string,
            )}{' '}
            {
              task?.pointOfCareTest?.criticalValueConfirmations[0]?.user
                ?.fullName
            }{' '}
            confirmed critical value
          </p>
        );
      case 2:
        return (
          <p>
            {Enums.humanize(
              task?.pointOfCareTest?.criticalValueConfirmations[0]
                ?.operatingAsRole as string,
            )}{' '}
            {
              task?.pointOfCareTest?.criticalValueConfirmations[0]?.user
                ?.fullName
            }{' '}
            and{' '}
            {Enums.humanize(
              task?.pointOfCareTest?.criticalValueConfirmations[1]
                ?.operatingAsRole as string,
            )}{' '}
            {
              task?.pointOfCareTest?.criticalValueConfirmations[1]?.user
                ?.fullName
            }{' '}
            confirmed critical value
          </p>
        );
    }
  };

  const renderTestName = () => {
    switch (lookupId) {
      case POCTestCategoryToLookupIdEnum.Covid:
        return 'COVID';
      case POCTestCategoryToLookupIdEnum.Glucose:
        return 'glucose';
      default:
        return 'this test';
    }
  };

  const getConfirmationIsVisible = () => {
    if (operatingAsClinician) {
      return Boolean(
        result &&
          resultIsCritical &&
          task?.status &&
          [
            EncounterTaskStatusEnum.IN_PROGRESS,
            EncounterTaskStatusEnum.NOT_STARTED,
            EncounterTaskStatusEnum.RESPONDER_COMPLETED,
          ].includes(task.status),
      );
    } else {
      return Boolean(
        result &&
          resultIsCritical &&
          task?.status &&
          [
            EncounterTaskStatusEnum.IN_PROGRESS,
            EncounterTaskStatusEnum.NOT_STARTED,
          ].includes(task.status),
      );
    }
  };

  return (
    <>
      <TaskHeader
        {...taskHeaderProps}
        correctionsWorkflow={correctionsWorkflow}
        setCorrectionsWorkflow={setCorrectionsWorkflow}
      />
      <StyledClinicianAttestation>
        {renderClinicianAttestation(task as EncounterTask)}
      </StyledClinicianAttestation>
      <StyledTaskForm
        onSubmit={(e) => e.preventDefault()}
        autoComplete="off"
        noValidate
        style={{ paddingBottom: `calc(${Tokens.rhythm} * 6)` }}
      >
        <header>
          <h3>{task?.taskName}</h3>

          <p className="formGeneralNote">
            All fields below, unless otherwise noted, are required.
          </p>

          {isOrderedModule || !hasStandingOrderOptions ? (
            <p className="formInstructionsLabel">
              1. Document ordering clinician’s name
            </p>
          ) : (
            <p className="formInstructionsLabel">
              1. Review/add the relevant Standing Order for the encounter
            </p>
          )}
        </header>

        <section>
          {isOrderedModule || !hasStandingOrderOptions ? (
            <>
              <Controlled.Input
                control={control}
                defaultValue=""
                disabled={inputsAreDisabledAndNotCorrectable}
                fullWidth
                label="First Name"
                name="clinicianFirstName"
              />

              <Controlled.Input
                control={control}
                defaultValue=""
                disabled={inputsAreDisabledAndNotCorrectable}
                fullWidth
                label="Last Name"
                name="clinicianLastName"
              />
            </>
          ) : (
            <>
              <StaticStandingOrders
                disabled={inputsAreDisabledAndNotCorrectable}
                hasError={Boolean(errors.standingOrderId)}
                standingOrderId={standingOrderId}
                standingOrderOptions={
                  task?.pointOfCareTestLookup?.standingOrders
                }
                standingOrderAddedAt={standingOrderAddedAt}
                onAddStandingOrder={(order) => {
                  setValue('standingOrderId', order.standingOrderId!, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue('standingOrderAddedAt', new Date().toISOString(), {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue('clinicianFirstName', order.clinician?.firstName, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue('clinicianLastName', order.clinician?.lastName, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                onDeleteStandingOrder={() => {
                  setValue('standingOrderId', null, { shouldDirty: true });
                  setValue('standingOrderAddedAt', null, {
                    shouldDirty: true,
                  });
                  setValue('clinicianFirstName', null, {
                    shouldDirty: true,
                  });
                  setValue('clinicianLastName', null, {
                    shouldDirty: true,
                  });
                }}
              />
              {errors.standingOrderId ? (
                <p style={{ color: Tokens.color.ui.error.base }}>
                  {errors.standingOrderId.message}
                </p>
              ) : null}
            </>
          )}
        </section>

        <p className="formInstructionsLabel">
          2. Confirm quality controls pass
        </p>

        <section>
          <Controlled.Input
            control={control}
            defaultValue=""
            disabled={inputsAreDisabledOrNoClinician}
            fullWidth
            label="Room/Ambient Temperature"
            name="temperature"
            rightLabel="&deg;F"
            type="number"
          />

          {outOfRangeTemperature && (
            <div style={{ marginTop: `calc(${Tokens.rhythm} * 2)` }}>
              <UI.AlertBox
                label="Out of range temperature"
                message={outOfRangeAlertMessage('temperature')}
                severity={UI.AlertBox.SeverityLevel.Danger}
              />
            </div>
          )}

          <Controlled.Input
            control={control}
            defaultValue=""
            disabled={inputsAreDisabledOrNoClinician}
            fullWidth
            label="Room/Ambient Humidity"
            name="humidity"
            onKeyDown={(event) =>
              event.key !== '.' ? event.key : event.preventDefault()
            } // Do not allow decimals
            rightLabel="%"
            type="number"
          />

          {outOfRangeHumidity && (
            <div style={{ marginTop: `calc(${Tokens.rhythm} * 2)` }}>
              <UI.AlertBox
                label="Out of range humidity"
                message={outOfRangeAlertMessage('humidity')}
                severity={UI.AlertBox.SeverityLevel.Danger}
              />
            </div>
          )}

          <Controlled.Datepicker
            control={control}
            disabled={inputsAreDisabledOrNoClinician}
            fullWidth
            label="Reagent Expiration Date"
            name="expirationDate"
            format="MM/dd/yyyy"
            timeZone="UTC"
          />

          {reagentIsExpired && (
            <div style={{ marginTop: `calc(${Tokens.rhythm} * 2)` }}>
              <UI.AlertBox
                label="Discard expired reagent"
                message="Because this reagent has expired, you must discard the expired reagent."
                severity={UI.AlertBox.SeverityLevel.Danger}
              />
            </div>
          )}
        </section>

        <p className="formInstructionsLabel">
          3. Document test details and administer test to patient
        </p>

        <section>
          <Controlled.Input
            control={control}
            defaultValue=""
            disabled={qualityControlsAreFailing}
            fullWidth
            label="Lot #"
            name="lotNumber"
          />

          <Controlled.RadioButtonGroup
            control={control}
            disabled={qualityControlsAreFailing}
            label="Is the internal control acceptable?"
            name="internalControlAcceptable"
            options={['Yes', 'No']}
          />

          {typeof internalControlAcceptable === 'boolean' &&
            !internalControlAcceptable && (
              <div style={{ marginTop: `calc(${Tokens.rhythm} * 2)` }}>
                <UI.AlertBox
                  label="Internal controls not acceptable"
                  message={internalControlsAlertMessage}
                  severity={UI.AlertBox.SeverityLevel.Danger}
                />
              </div>
            )}

          {renderResultTypePerPOCTLookupId()}

          {result && resultIsCritical
            ? renderCriticalValueConfirmation()
            : null}

          {getConfirmationIsVisible() ? (
            <div style={{ marginTop: `calc(${Tokens.rhythm} * 2)` }}>
              <UI.AlertBox
                label="Critical value confirmation"
                message={
                  <Controlled.Checkbox
                    control={control}
                    disabled={qualityControlsAreFailing}
                    name="criticalValueConfirmation"
                    label={`I attest this patient’s result for ${renderTestName()} is critical`}
                  />
                }
                severity={UI.AlertBox.SeverityLevel.Danger}
              />
            </div>
          ) : null}

          <Controlled.ImageInput
            className="testResultAttachment"
            control={control}
            description="Test Result"
            disabled={qualityControlsAreFailing}
            folder={`${task?.encounter?.encounterId}`}
            fullWidth
            label="Photo Documentation"
            name="testResultAttachment"
          />
        </section>
        {task?.encounterTaskId && (
          <AdditionalPhotoDocSection
            encounterTaskId={task?.encounterTaskId}
            disabled={inputsAreDisabled}
          />
        )}
      </StyledTaskForm>
    </>
  );
};

export default PointOfCareTestForm;
